import { isLowEndPC } from "@/utils/deviceCheck";

let configPromise = null;
let enabled = null;

function loadConfig() {
  if (!configPromise) {
    configPromise = fetch("/api/firebase-config")
      .then((res) => (res.ok ? res.json() : null))
      .then((cfg) => {
        enabled = Boolean(cfg && cfg.projectId && cfg.apiKey && cfg.appId);
        return enabled ? cfg : null;
      })
      .catch(() => {
        enabled = false;
        return null;
      });
  }
  return configPromise;
}

const ENGAGE_MS = 15_000;
const RETENTION_MS = 90 * 24 * 3600 * 1000;
const MAX_SESSION_EVENTS = 40;
const MAX_QUEUE = 80;
const MAX_STORED_KEYS = 200;
const SAMPLE_MAX = 160;
const SIG_MAX = 96;
const STORE_KEY = "lyszt_telemetry_dedup";
const PARK_KEY = "lyszt_vitals_park";

const cache = new Set();
const queue = [];
let visibleMs = 0;
let sinceTick = null;
let interacted = false;
let gateOpen = false;
let tracking = false;
let gateTimer = null;

const sigOf = (message) =>
  String(message)
    .toLowerCase()
    .replace(/'[^']*'|"([^"]*)"/g, "s")
    .replace(/0x[0-9a-f]+/g, "#")
    .replace(/\d+/g, "#")
    .replace(/[!?.,;:]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, SIG_MAX);

const today = () => new Date().toISOString().slice(0, 10);

const expiresAt = () => Date.now() + RETENTION_MS;

// Reads the persisted day-scoped dedup set, dropping entries from previous days.
function readSent() {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = JSON.parse(window.localStorage.getItem(STORE_KEY) ?? "{}");
    const day = today();
    const keys = new Set();
    for (const [key, sent] of Object.entries(raw)) if (sent === day) keys.add(key);
    return keys;
  } catch {
    return new Set();
  }
}

function markSent(key) {
  if (typeof window === "undefined") return;
  try {
    const day = today();
    const raw = JSON.parse(window.localStorage.getItem(STORE_KEY) ?? "{}");
    for (const k of Object.keys(raw)) if (raw[k] !== day) delete raw[k];
    if (Object.keys(raw).length < MAX_STORED_KEYS) {
      raw[key] = day;
      window.localStorage.setItem(STORE_KEY, JSON.stringify(raw));
    }
  } catch {
    // storage unavailable: the in-memory cache still dedups this session
  }
}

// A session becomes a writer after ENGAGE_MS of visible time and at least one
// input event; shorter visits queue events and drop them on unload.
function engaged() {
  if (gateOpen) return true;
  const visible = visibleMs + (sinceTick !== null ? Date.now() - sinceTick : 0);
  if (!interacted || visible < ENGAGE_MS) return false;
  gateOpen = true;
  if (gateTimer !== null) clearInterval(gateTimer);
  return true;
}

function trackEngagement() {
  if (tracking || typeof window === "undefined") return;
  tracking = true;
  const doc = window.document;
  if (doc?.visibilityState === "visible") sinceTick = Date.now();
  doc?.addEventListener?.("visibilitychange", () => {
    if (doc.visibilityState === "visible") sinceTick = Date.now();
    else if (sinceTick !== null) {
      visibleMs += Date.now() - sinceTick;
      sinceTick = null;
    }
  });
  const onInteract = () => { interacted = true; };
  for (const evt of ["pointerdown", "keydown", "wheel", "touchstart"]) {
    window.addEventListener?.(evt, onInteract, { passive: true });
  }
  gateTimer = setInterval(flushQueue, 2000);
}

let dbPromise = null;

// Resolves to [firestore, firestoreApi] or null when Firebase is unset or failed to load.
function getDb() {
  if (!dbPromise) {
    dbPromise = Promise.all([loadConfig(), import("firebase/app"), import("firebase/firestore")])
      .then(([cfg, app, fs]) => {
        if (!cfg) return null;
        const instance = app.initializeApp({ projectId: cfg.projectId, apiKey: cfg.apiKey, appId: cfg.appId });
        return [fs.getFirestore(instance), fs];
      })
      .catch(() => null);
  }
  return dbPromise;
}

function enqueue(item) {
  if (queue.length >= MAX_QUEUE) return;
  queue.push(item);
  flushQueue();
}

function flushQueue() {
  if (!engaged() || !queue.length) return;
  const db = getDb();
  const batch = queue.splice(0, queue.length);
  if (!db) return;
  db.then((handle) => {
    if (!handle) return;
    const [firestore, fs] = handle;
    for (const item of batch) {
      try {
        if (item.dedupKey) markSent(item.dedupKey);
        fs.setDoc(fs.doc(firestore, item.coll, item.day, "entries", item.docId), item.data(fs), { merge: true }).catch(() => {});
      } catch {
        // one malformed item does not drop the rest of the batch
      }
    }
  }).catch(() => {});
}

// Queues a day-bucketed counter event keyed by kind + normalized message; the
// write happens once the session passes the engagement gate, at most once per
// day per browser.
export function reportEvent(kind, message) {
  if (enabled === false) return;
  loadConfig();
  trackEngagement();
  const sig = sigOf(message);
  const key = `${kind}:${sig}`;
  if (cache.has(key)) return;
  const persisted = readSent();
  if (persisted.has(key)) { cache.add(key); return; }
  if (cache.size >= MAX_SESSION_EVENTS) return;
  cache.add(key);
  enqueue({
    coll: "events",
    day: today(),
    docId: key.replace(/[^-a-z0-9_]/g, "_").slice(0, 500),
    dedupKey: key,
    data: (fs) => ({
      count: fs.increment(1),
      kind,
      sample: String(message).slice(0, SAMPLE_MAX),
      page: (typeof window !== "undefined" ? window.location.pathname : "").slice(0, 120),
      v: 1,
      expiresAt: fs.Timestamp.fromMillis(expiresAt()),
      lastSeen: fs.serverTimestamp(),
    }),
  });
}

export function registerCrashHandlers() {
  if (typeof window === "undefined" || window.__crashHandlers) return;
  window.__crashHandlers = true;
  window.addEventListener("error", (e) => {
    reportEvent("crash", e?.message ?? "uncaught error");
  });
  window.addEventListener("unhandledrejection", (e) => {
    reportEvent("crash", e?.reason?.message ?? "unhandled rejection");
  });
}

// ── Web vitals: histogram counters per metric, bin and device class ──
// Firestore cannot beacon on pagehide, so each session parks its final metric
// values in localStorage and the next engaged session writes them.

const deviceClass = () => {
  if (typeof navigator === "undefined") return "std";
  if (isLowEndPC()) return "low";
  if (/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) return "mob";
  return "std";
};

const binOf = (metric, value) =>
  metric === "cls" ? Math.min(Math.round(value * 20), 16) : Math.min(Math.round(value / 200), 32);

function reportVital(day, metric, value, cls, page) {
  const bin = binOf(metric, value);
  enqueue({
    coll: "vitals",
    day,
    docId: `${metric}_${bin}_${cls}_${page.replace(/[^-a-z0-9_]/g, "_").slice(0, 80)}`,
    dedupKey: null,
    data: (fs) => ({
      count: fs.increment(1),
      sample: metric === "cls" ? String(value.toFixed(3)) : `${Math.round(value)}ms`,
      page: page.slice(0, 120),
      v: 1,
      expiresAt: fs.Timestamp.fromMillis(expiresAt()),
      lastSeen: fs.serverTimestamp(),
    }),
  });
}

function flushParked() {
  let park;
  try {
    park = JSON.parse(window.localStorage.getItem(PARK_KEY) ?? "null");
    window.localStorage.removeItem(PARK_KEY);
  } catch {
    return;
  }
  if (!park?.page || !park?.cls) return;
  if (typeof park.lcp === "number") reportVital(park.day, "lcp", park.lcp, park.cls, park.page);
  if (typeof park.inp === "number") reportVital(park.day, "inp", park.inp, park.cls, park.page);
  if (typeof park.clsScore === "number") reportVital(park.day, "cls", park.clsScore, park.cls, park.page);
}

export function initVitals() {
  if (typeof window === "undefined" || window.__vitalsInit) return;
  window.__vitalsInit = true;
  trackEngagement();
  flushParked();
  const latest = {};
  import("web-vitals").then((wv) => {
    wv.onLCP?.((m) => { latest.lcp = m.value; });
    wv.onINP?.((m) => { latest.inp = m.value; });
    wv.onCLS?.((m) => { latest.cls = m.value; });
  }).catch(() => {});
  const page = window.location.pathname;
  const cls = deviceClass();
  const park = () => {
    try {
      window.localStorage.setItem(PARK_KEY, JSON.stringify({
        day: today(),
        page,
        cls,
        lcp: latest.lcp,
        inp: latest.inp,
        clsScore: latest.cls,
      }));
    } catch {
      // storage unavailable: this session's vitals are not carried over
    }
  };
  window.addEventListener("pagehide", park);
  window.document?.addEventListener?.("visibilitychange", () => {
    if (window.document.visibilityState === "hidden") park();
  });
}
